import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { formatMoney } from '@theme/money-formatting';

/**
 * Barra «quanto manca alla spedizione gratuita» (snippets/parrot-spedizione-gratis.liquid).
 *
 * Liquid scrive lo stato iniziale. Qui si aggiorna a ogni cambio del carrello: se l'evento porta il carrello
 * intero (modifiche dal carrello, codici sconto) si usa quello, altrimenti (aggiungi al carrello) si rilegge
 * /cart.js. Una richiesta nuova annulla quella vecchia, così una risposta lenta non sovrascrive una più recente.
 *
 * @typedef {object} Refs
 * @property {HTMLParagraphElement} message - Il testo, che è anche la regione di stato per i lettori di schermo.
 * @property {HTMLTemplateElement} [moneyFormat] - Formato prezzi del negozio, lo stesso del filtro `money`.
 *
 * @extends {Component<Refs>}
 */
class ParrotFreeShipping extends Component {
  requiredRefs = ['message'];

  /** @type {AbortController | null} */
  #listeners = null;

  /** @type {AbortController | null} */
  #request = null;

  connectedCallback() {
    super.connectedCallback();

    this.#listeners?.abort();
    this.#listeners = new AbortController();
    const { signal } = this.#listeners;

    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartChange, { signal });
    document.addEventListener(ThemeEvents.discountUpdate, this.#onCartChange, { signal });
    window.addEventListener('pageshow', this.#onPageShow, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#listeners?.abort();
    this.#listeners = null;
    this.#request?.abort();
    this.#request = null;
  }

  /**
   * @param {Event} event
   */
  #onCartChange = (event) => {
    const cart = /** @type {any} */ (event).detail?.resource;

    if (isCart(cart)) {
      this.#request?.abort();
      this.render(cart);
      return;
    }

    this.#refresh();
  };

  /**
   * Pagina ripresa dalla cache del browser (tasto indietro): il carrello può essere cambiato.
   * @param {PageTransitionEvent} event
   */
  #onPageShow = (event) => {
    if (event.persisted) this.#refresh();
  };

  async #refresh() {
    this.#request?.abort();
    const controller = new AbortController();
    this.#request = controller;

    try {
      const response = await fetch(`${this.dataset.cartUrl || '/cart'}.js`, {
        signal: controller.signal,
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) return;

      const cart = await response.json();
      if (controller.signal.aborted || !isCart(cart)) return;

      this.render(cart);
    } catch (error) {
      if (/** @type {Error} */ (error)?.name !== 'AbortError') console.error(error);
    } finally {
      if (this.#request === controller) this.#request = null;
    }
  }

  /**
   * Aggiorna testo, barra e visibilità a partire da un carrello in formato /cart.js.
   * @param {{ item_count: number, total_price: number, currency?: string, requires_shipping?: boolean }} cart
   */
  render(cart) {
    const threshold = Number(this.dataset.threshold);
    if (!Number.isFinite(threshold) || threshold <= 0) return;

    const currency = this.dataset.currency || 'EUR';

    if (cart.currency && cart.currency !== currency) {
      this.hidden = true;
      return;
    }

    const count = cart.item_count;
    const total = cart.total_price;

    this.hidden = count > 0 && cart.requires_shipping === false;

    /** @type {'empty' | 'missing' | 'done'} */
    let state;
    let progress;

    if (count === 0) {
      state = 'empty';
      progress = 0;
    } else if (total >= threshold) {
      state = 'done';
      progress = 1;
    } else {
      state = 'missing';
      // mai barra piena finché manca anche un centesimo, come nello snippet
      progress = Math.min(0.98, Math.round((total / threshold) * 1000) / 1000);
    }

    this.dataset.state = state;
    this.style.setProperty('--parrot-sg-progress', String(progress));

    switch (state) {
      case 'empty':
        this.#setMessage(this.dataset.textEmpty ?? '', '[soglia]', this.#money(threshold, currency));
        break;
      case 'missing':
        this.#setMessage(this.dataset.textMissing ?? '', '[importo]', this.#money(threshold - total, currency));
        break;
      default:
        this.#setMessage(this.dataset.textDone ?? '', '', '');
    }
  }

  /**
   * Scrive il messaggio con l'importo in grassetto, senza innerHTML. Se il testo è lo stesso non tocca niente,
   * così il lettore di schermo non lo ripete.
   * @param {string} template
   * @param {string} placeholder
   * @param {string} amount
   */
  #setMessage(template, placeholder, amount) {
    const { message } = this.refs;
    const parts = placeholder ? template.split(placeholder) : [template];
    const text = parts.join(amount);

    if (message.textContent?.trim() === text.trim()) return;

    /** @type {Node[]} */
    const nodes = [];
    parts.forEach((part, index) => {
      if (index > 0) {
        const strong = document.createElement('strong');
        strong.textContent = amount;
        nodes.push(strong);
      }
      if (part) nodes.push(document.createTextNode(part));
    });

    message.replaceChildren(...nodes);
  }

  /**
   * @param {number} cents
   * @param {string} currency
   */
  #money(cents, currency) {
    const format = this.refs.moneyFormat?.content.textContent?.trim() || '{{amount_with_comma_separator}} €';
    return formatMoney(cents, format, currency);
  }
}

/**
 * @param {any} cart
 * @returns {cart is { item_count: number, total_price: number, currency?: string, requires_shipping?: boolean }}
 */
function isCart(cart) {
  return Boolean(cart) && typeof cart.item_count === 'number' && typeof cart.total_price === 'number';
}

if (!customElements.get('parrot-free-shipping')) {
  customElements.define('parrot-free-shipping', ParrotFreeShipping);
}
